import { defineComponent, h } from 'vue'
import { QIcon } from 'quasar'

import BsBtn, { bsBtnProps, useBsBtn } from './BsBtn'
import { quasarHandlers } from '../quasarHandlers'

/**
 * The "Filters" button, with a count when any are applied. Renders a BsBtn rather than a QBtn, so it is
 * the one member of this family that nests instead of replacing.
 */
export default defineComponent({
  name: 'BsFilterBtn',

  /**
   * The render already forwards `$attrs` itself, and it puts the fixed `data-cy` hook AFTER them so
   * the hook wins. Left inherited, Vue 3 re-applies the caller's attributes over the whole bag --
   * including onto BsBtn -- and a caller's own `data-cy` replaced `btnFilters`, which is what the
   * e2e selectors look for.
   */
  inheritAttrs: false,

  props: {
    ...bsBtnProps,
    filterCount: { type: Number, default: void 0 },
    filtersShowing: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    return { ...useBsBtn(props, emit) }
  },

  methods: {
    // Renamed from `__renderX`: Vue does not proxy keys beginning with `_` onto the instance.
    renderIcon () {
      return h(QIcon, {
        name: 'app:filter',
        size: '1.125rem'
      })
    },

    renderText () {
      const text = this.filterCount > 0
        ? this.$tc('system.actions.filtersX', void 0, [this.filterCount])
        : this.$tc('system.actions.filters')

      return h('strong', text)
    },

    getProps (): Record<string, unknown> {
      return {
        textColor: 'black'
      }
    }
  },

  render () {
    return h(BsBtn, {
      class: ['bs-filter-btn', {
        'bs-filter-btn__active': this.filtersShowing
      }],
      ...this.getBtnProps(),
      ...this.getProps(),
      ...quasarHandlers(this.getListeners()),
      ...this.getAttributes({
        ...this.$attrs,
        'data-cy': 'btnFilters'
      })
    // BsBtn is a component: children have to be a slot function.
    }, { default: () => [
      this.renderIcon(),
      this.renderText()
    ] })
  }
})
